import type { ScormData } from '~/utils/scorm'
import { useMutation, useQuery } from '@apollo/client/react'
import { useCallback, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { useParams } from 'react-router-dom'
import { GQL_GET_MY_SCORM_TRACKING, GQL_UPSERT_SCORM_TRACKING } from '~/gql/scorm/scorm'
import { GQL_MY_SESSIONS } from '~/gql/sessions/sessions'
import { useAuth } from '~/helpers/store/auth'
import LearnerUserMenu from '~/pages/components/LearnerUserMenu'
import ScormShell from './ScormShell'

function ScormPlayer() {
  const { t } = useTranslation()
  const { sessionId, moduleId } = useParams()

  // Pendant une impersonation, le module se joue mais rien n'est persisté :
  // la progression de l'admin ne doit pas polluer les stats de l'apprenant.
  // L'API rejette de toute façon les upserts des sessions impersonées.
  const isImpersonating = useAuth(s => Boolean(s.payload?.impersonatorId))

  // Résout le module via les parcours de l'apprenant (gating implicite : un
  // parcours non inscrit n'apparaît pas ici).
  const { data: sessionsData, loading: sessionsLoading } = useQuery(GQL_MY_SESSIONS, {
    fetchPolicy: 'cache-and-network',
  })
  const session = sessionsData?.mySessions?.find(s => s.id === sessionId)
  const step = session?.modules?.find(m => m.module?.id === moduleId)

  // Un module fermé ne se joue pas, même si son URL est devinée. L'API refuse
  // déjà le contenu ET l'écriture de progression (#182) ; ce test-ci évite
  // seulement d'afficher un lecteur qui ne chargerait rien.
  const module = step?.unlocked ? step.module : undefined

  const { data, loading: trackingLoading } = useQuery(GQL_GET_MY_SCORM_TRACKING, {
    variables: { sessionId: sessionId ?? '', moduleId },
    skip: !sessionId || !moduleId,
    fetchPolicy: 'network-only',
  })

  const [upsertTracking] = useMutation(GQL_UPSERT_SCORM_TRACKING)

  const handleCommit = useCallback(async (scormData: ScormData) => {
    if (!sessionId || !moduleId || isImpersonating) return
    // Le module est désigné explicitement : sans lui, l'API rattacherait la
    // progression au PREMIER module du parcours, et les cinq modules d'un
    // parcours écriraient tous sur la même ligne.
    const input: { sessionId: string, moduleId: string, [key: string]: unknown } = { sessionId, moduleId }

    if (scormData.completionStatus) input.completionStatus = scormData.completionStatus
    if (scormData.successStatus) input.successStatus = scormData.successStatus
    if (scormData.scoreRaw != null) input.scoreRaw = scormData.scoreRaw
    if (scormData.scoreMin != null) input.scoreMin = scormData.scoreMin
    if (scormData.scoreMax != null) input.scoreMax = scormData.scoreMax
    if (scormData.scoreScaled != null) input.scoreScaled = scormData.scoreScaled
    if (scormData.totalTime) input.totalTime = scormData.totalTime
    if (scormData.sessionTime) input.sessionTime = scormData.sessionTime
    if (scormData.suspendData) input.suspendData = scormData.suspendData
    if (scormData.location) input.location = scormData.location

    await upsertTracking({ variables: { input } })
  }, [upsertTracking, sessionId, moduleId, isImpersonating])

  const initialData = useMemo<ScormData | null>(() => data?.getMyScormTracking
    ? {
        completionStatus: data.getMyScormTracking.completionStatus as string,
        successStatus: data.getMyScormTracking.successStatus as string,
        scoreRaw: data.getMyScormTracking.scoreRaw,
        scoreMin: data.getMyScormTracking.scoreMin,
        scoreMax: data.getMyScormTracking.scoreMax,
        scoreScaled: data.getMyScormTracking.scoreScaled,
        totalTime: data.getMyScormTracking.totalTime,
        sessionTime: data.getMyScormTracking.sessionTime,
        suspendData: data.getMyScormTracking.suspendData,
        location: data.getMyScormTracking.location,
      }
    : null, [data])

  return (
    <ScormShell
      loading={sessionsLoading || trackingLoading}
      module={module}
      initialData={initialData}
      onCommit={handleCommit}
      headerContent={(
        <>
          {session && (
            <span className="text-sm text-neutral-300">
              {session.name}
              {/* Le rang n'apparaît que sur un vrai parcours : « 2/5 » à côté
                  d'un parcours à un module n'informerait de rien. */}
              {(session.modules?.length ?? 0) > 1 && step && (
                <span className="ml-2 text-neutral-500">
                  {t('scorm.player.step', {
                    current: step.position + 1,
                    total: session.modules!.length,
                  })}
                </span>
              )}
            </span>
          )}
          {isImpersonating && (
            <span className="rounded-full bg-amber-500/15 px-2 py-0.5 text-xs font-medium text-amber-400">
              {t('scorm.impersonate.noTracking')}
            </span>
          )}
        </>
      )}
      headerRight={<LearnerUserMenu variant="dark" showBackToSessions />}
      fallbackTo="/my-sessions"
      fallbackLabel={t('scorm.player.backToSessions')}
      unavailableMessage={t('scorm.player.unavailable')}
    />
  )
}

export default ScormPlayer
